import { useTranslation } from "react-i18next";
import { Info, Layers, Split, Check } from "lucide-react";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { ImageEffectCard } from "./ImageEffectCard";
import type { VideoImage } from "./video-form-utils";
import { cn } from "@/lib/utils";
import type { VideoFormat } from "@/lib/hooks/useImageUpload";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface MediaUploadStepProps {
  uploadedCount: number;
  maxImages: number;
  minImages: number;
  sensors: any;
  images: VideoImage[];
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleEffectChange: (imageId: string, effect: string) => void;
  handleImageUpload: (slotId: string, files: File[]) => void;
  handleImageRemove: (sharedId: string) => void;
  isSubmitting: boolean;
  prevStep: () => void;
  nextStep: () => void;
  canGoToStep3: boolean;
  format?: VideoFormat;
  useSplitFormat?: boolean;
  setUseSplitFormat?: (val: boolean) => void;
  totalSelectedFormats?: number;
  isFirstUploadStep?: boolean;
}

export const MediaUploadStep = ({
  uploadedCount,
  maxImages,
  minImages,
  sensors,
  images,
  handleDragStart,
  handleDragEnd,
  handleEffectChange,
  handleImageUpload,
  handleImageRemove,
  isSubmitting,
  prevStep,
  nextStep,
  canGoToStep3,
  format,
  useSplitFormat,
  setUseSplitFormat,
  totalSelectedFormats,
  isFirstUploadStep,
}: MediaUploadStepProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <h2 className="font-h3 text-xl 2k:text-4xl 4k:text-6xl">
              {format === "16:9" 
                ? t("createVideo.landscapeUploadTitle") 
                : format === "9:16"
                  ? t("createVideo.portraitUploadTitle")
                  : t("createVideo.chooseMovementTitle")}
            </h2>
            <p className="text-muted-foreground 2k:text-2xl 4k:text-4xl">
              {t("createVideo.uploadMinImages", { count: minImages })}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl 2k:text-5xl 4k:text-7xl font-bold">
              {uploadedCount} / {maxImages}
            </p>
            <p className="text-sm 2k:text-2xl 4k:text-4xl text-muted-foreground">
              {t("createVideo.imagesUploaded") || "Images uploaded"}
            </p>
          </div>
        </div>

        <div className="bg-black/3 p-5 rounded-2xl border border-black/5 flex flex-col sm:flex-row gap-4 sm:gap-8 items-start">
          <div className="flex items-center gap-2 text-black font-bold text-xs uppercase tracking-wider bg-white px-3 py-1.5 rounded-full border border-black/5 shadow-sm shrink-0">
            <Info className="size-3.5" />
            <span>{t("common.guide") || "Guide"}</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-12 flex-1">
            <div className="flex items-start gap-3">
              <div className="mt-1.5 size-1.5 rounded-full bg-black/20 shrink-0" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                <span className="font-bold text-black mr-1">Zoom:</span>
                {t("createVideo.zoomDescription")}
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-1.5 size-1.5 rounded-full bg-black/20 shrink-0" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                <span className="font-bold text-black mr-1">Panning:</span>
                {t("createVideo.panDescription")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={images.map((img) => img.id)}
            strategy={rectSortingStrategy}
          >
            <div className={cn(
              "grid gap-6 2k:gap-12 4k:gap-18",
              format === "9:16" 
                ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3" 
                : "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3"
            )}>
              {images.map((image, index) => (
                <ImageEffectCard
                  key={image.id}
                  id={image.id}
                  imageNumber={index + 1}
                  imageSrc={image.src}
                  selectedEffect={image.effect}
                  onEffectChange={(effect) =>
                    handleEffectChange(image.id, effect)
                  }
                  onFileUpload={(files) =>
                    handleImageUpload(image.id, files)
                  }
                  onRemove={
                    image.src
                      ? () => handleImageRemove(image.id)
                      : undefined
                  }
                  disabled={isSubmitting || image.disabled}
                  isDraggable={!!image.src && !image.isTemp}
                  isUploading={image.isTemp}
                  mediaType={image.mediaType}
                  format={format}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {totalSelectedFormats && totalSelectedFormats > 1 && isFirstUploadStep && (
        <div className="pt-8 mt-8 border-t border-black/5">
          <button
            type="button"
            onClick={() => setUseSplitFormat?.(!useSplitFormat)}
            className="flex items-center gap-6 group text-left"
          >
            <div className={cn(
              "w-14 h-7 rounded-full p-1 transition-all duration-300 relative shrink-0",
              useSplitFormat ? "bg-black shadow-lg" : "bg-black/5"
            )}>
              <div className={cn(
                "size-5 rounded-full bg-white transition-all duration-300 shadow-sm",
                useSplitFormat ? "translate-x-7" : "translate-x-0"
              )} />
            </div>
            <div className="space-y-1">
              <p className="font-bold text-base 2k:text-3xl 4k:text-5xl text-black group-hover:text-black/60 transition-colors">
                {t("createVideo.useSplitFormatSplitHint")}
              </p>
              <p className="text-sm 2k:text-2xl 4k:text-4xl text-muted-foreground">
                {t("createVideo.useSplitFormatSplit")}
              </p>
            </div>
          </button>
        </div>
      )}

      <div className="hidden lg:flex justify-between pt-8">
        <Button
          type="button"
          variant="outline"
          onClick={prevStep}
          className="rounded-full border-black/10 hover:bg-white px-12 py-7 2k:px-20 2k:py-12 4k:px-30 4k:py-18 text-lg 2k:text-3xl 4k:text-5xl cursor-pointer"
        >
          {t("common.back") || "Back"}
        </Button>
        <Button
          type="button"
          onClick={nextStep}
          disabled={!canGoToStep3}
          className="rounded-full bg-black hover:bg-black/90 text-white px-12 py-7 2k:px-20 2k:py-12 4k:px-30 4k:py-18 text-lg 2k:text-3xl 4k:text-5xl cursor-pointer disabled:opacity-30"
        >
          {t("common.next") || "Next"}
        </Button>
      </div>
    </div>
  );
};
